import { supabase } from "@/integrations/supabase/client";
import { Department } from "@/types/department";
import { Employee, EmployeeFilterOptions } from "@/types/employee";

export const createEmployee = async (employee: {
  name: string;
  email: string;
  position: string;
  departmentId: string;
  avatar?: string;
  phone?: string;
  bio?: string;
  startDate?: string;
  status?: "active" | "inactive" | "on-leave" | string;
  skills?: string[];
}): Promise<Employee> => {
  try {
    const { data, error } = await supabase
      .from("employees")
      .insert({
        name: employee.name,
        email: employee.email,
        position: employee.position,
        department_id: employee.departmentId,
        avatar: employee.avatar || null,
        phone: employee.phone || null,
        bio: employee.bio || null,
        start_date: employee.startDate || new Date().toISOString().split("T")[0],
        status: employee.status || "active",
      })
      .select()
      .single();

    if (error) throw error;

    // Add skills if provided
    if (employee.skills && employee.skills.length > 0) {
      const skillsToInsert = employee.skills.map((skill) => ({
        employee_id: data.id,
        skill,
      }));

      const { error: skillError } = await supabase
        .from("employee_skills")
        .insert(skillsToInsert);

      if (skillError) throw skillError;
    }

    // Create notification for employee creation
    try {
      const { data: userData } = await supabase.auth.getSession();
      const userId = userData.session?.user.id;
      
      if (userId) {
        const { error: notificationError } = await supabase
          .from("notifications")
          .insert([
            {
              user_id: userId,
              title: "Employee Created",
              message: `You added a new employee: ${employee.name}`,
              type: "employee_created",
              is_read: false
            }
          ]);
          
        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      }
    } catch (notificationError) {
      console.error("Failed to create notification:", notificationError);
    }

    const newEmployee: Employee = {
      id: data.id,
      name: data.name,
      email: data.email,
      position: data.position,
      departmentId: data.department_id,
      avatar: data.avatar,
      phone: data.phone,
      bio: data.bio,
      start_date: data.start_date,
      status: data.status
    };

    return newEmployee;
  } catch (error) {
    console.error("Error creating employee:", error);
    throw error;
  }
};

export const updateEmployee = async (
  id: string,
  employee: Partial<{
    name: string;
    email: string;
    position: string;
    departmentId: string;
    avatar: string;
    phone: string;
    bio: string;
    startDate: string;
    status: "active" | "inactive" | "on-leave" | string;
  }>
): Promise<void> => {
  try {
    const updates: any = {};

    if (employee.name) updates.name = employee.name;
    if (employee.email) updates.email = employee.email;
    if (employee.position) updates.position = employee.position;
    if (employee.departmentId) updates.department_id = employee.departmentId;
    if (employee.avatar !== undefined) updates.avatar = employee.avatar;
    if (employee.phone !== undefined) updates.phone = employee.phone;
    if (employee.bio !== undefined) updates.bio = employee.bio;
    if (employee.startDate) updates.start_date = employee.startDate;
    if (employee.status) updates.status = employee.status;

    const { error } = await supabase
      .from("employees")
      .update(updates)
      .eq("id", id);

    if (error) throw error;

    // Create notification for employee update
    try {
      const { data: userData } = await supabase.auth.getSession();
      const userId = userData.session?.user.id;
      
      if (userId) {
        let notificationType = "employee_updated";
        let notificationTitle = "Employee Updated";
        let notificationMessage = `You updated employee: ${employee.name || ""}`;
        
        // If updating status, send a specific notification for that
        if (employee.status) {
          notificationType = "employee_status_changed";
          notificationTitle = "Employee Status Changed";
          notificationMessage = `You changed the status of ${employee.name || "an employee"} to ${employee.status}`;
        }
        
        const { error: notificationError } = await supabase
          .from("notifications")
          .insert([
            {
              user_id: userId,
              title: notificationTitle,
              message: notificationMessage,
              type: notificationType,
              is_read: false
            }
          ]);
          
        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      }
    } catch (notificationError) {
      console.error("Failed to create notification:", notificationError);
    }
  } catch (error) {
    console.error("Error updating employee:", error);
    throw error;
  }
};

export const fetchEmployees = async (filters?: EmployeeFilterOptions): Promise<Employee[]> => {
  try {
    let query = supabase.from("employees").select("*");

    // Apply filters if provided
    if (filters) {
      if (filters.department && filters.department !== 'all') {
        query = query.eq('department_id', filters.department);
      }
      
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,position.ilike.%${filters.search}%`);
      }
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    // Map database response to Employee type
    const employees: Employee[] = data.map(emp => ({
      id: emp.id,
      name: emp.name,
      email: emp.email,
      position: emp.position,
      departmentId: emp.department_id,
      avatar: emp.avatar,
      phone: emp.phone,
      bio: emp.bio,
      start_date: emp.start_date,
      status: emp.status
    }));
    
    return employees;
  } catch (error) {
    console.error("Error fetching employees:", error);
    return [];
  }
};

export const fetchEmployeeById = async (id: string): Promise<Employee | null> => {
  try {
    const { data, error } = await supabase
      .from("employees")
      .select("*, employee_skills(*)")
      .eq("id", id)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      email: data.email,
      position: data.position,
      departmentId: data.department_id,
      avatar: data.avatar,
      phone: data.phone,
      bio: data.bio,
      start_date: data.start_date,
      status: data.status,
      skills: data.employee_skills
    };
  } catch (error) {
    console.error("Error fetching employee by ID:", error);
    return null;
  }
};

export const deleteEmployee = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.from("employees").delete().eq("id", id);
    if (error) throw error;
  } catch (error) {
    console.error("Error deleting employee:", error);
  }
};
